import { useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { InboxDialog } from './inbox-dialog';
import { InboxFacts } from './inbox-facts';
import { InboxSharingDialog } from './inbox-sharing-dialog';
import { PrimaryChangeConfirm } from './primary-change-confirm';
import { useInboxes, useInboxMutations } from '@/hooks/use-inboxes';
import { useBusinessUnits } from '@/hooks/use-business-units';
import { belongsToUnit, inboxUnitIds, isPrimaryUnit, isSharedInbox } from '../inbox-units';
import type { BusinessUnit, Inbox } from '@/types';

interface Props {
  businessUnit: BusinessUnit;
}

/**
 * The inboxes under one business unit — as primary or as an extra. Rendered only while its
 * row is expanded.
 *
 * Each inbox still shows its one destination inline: `inboxes.channel_id` is a single
 * column, so an inbox answers for exactly one destination no matter how many units it
 * belongs to. What can now be plural is the units themselves, via `bu_id` (primary) and
 * `shared_bu_ids` (extras) — see `inbox-units.ts` for what that makes "shared" mean.
 */
export function InboxesSubsection({ businessUnit }: Props) {
  const { data: all = [], isLoading, error } = useInboxes();
  const { data: allUnits = [] } = useBusinessUnits();
  const {
    clearBusinessUnit,
    deleteInbox,
    shareWithBusinessUnit,
    unshareFromBusinessUnit,
    setPrimaryBusinessUnit
  } = useInboxMutations();
  const [busyId, setBusyId] = useState<number | null>(null);

  // Machine refuses to share an inbox with an inactive unit or make one primary, so neither
  // is offered under one. Its inboxes can still be shared onward, unassigned and deleted.
  const unitIsActive = businessUnit.status === 'active';

  const inboxes = all.filter((i) => belongsToUnit(i, businessUnit.id));
  // Offered by "Add existing inbox" — anything not already here, primary or shared. An
  // inbox that belongs to a different unit is a valid pick too: it just becomes shared.
  const shareable = all.filter((i) => !belongsToUnit(i, businessUnit.id));

  const shareExisting = async (inboxId: string) => {
    const inbox = shareable.find((i) => String(i.id) === inboxId);
    if (!inbox) return;
    setBusyId(inbox.id);
    try {
      const hadNoUnit = inboxUnitIds(inbox).length === 0;
      await shareWithBusinessUnit(inbox.id, businessUnit.id);
      toast.success(
        hadNoUnit
          ? `${inbox.name} is now under ${businessUnit.name}`
          : `${inbox.name} is now shared with ${businessUnit.name}`
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not share inbox');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Detaches this ONE unit from the inbox. A shared inbox (2+ units) keeps the rest and
   * only loses this one — sending it through `clearBusinessUnit` would wipe every unit at
   * once, which is what that call is for on a single-unit inbox, not this.
   */
  const unassign = async (inbox: Inbox) => {
    setBusyId(inbox.id);
    try {
      if (isSharedInbox(inbox)) {
        await unshareFromBusinessUnit(inbox.id, businessUnit.id);
      } else {
        await clearBusinessUnit(inbox.id);
      }
      toast.success(`${inbox.name} is no longer under ${businessUnit.name}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not unassign');
    } finally {
      setBusyId(null);
    }
  };

  const makePrimary = async (inbox: Inbox) => {
    setBusyId(inbox.id);
    try {
      await setPrimaryBusinessUnit(inbox.id, businessUnit.id);
      toast.success(`${businessUnit.name} is now the primary unit for ${inbox.name}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Machine refuses while conversations point at the inbox, and its 409 names how many.
   * That message is relayed verbatim: a rewording here would either lose the count or
   * drift from what the server actually enforces.
   */
  const remove = async (inboxId: number, name: string) => {
    setBusyId(inboxId);
    try {
      await deleteInbox(inboxId);
      toast.success(`${name} deleted`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className='space-y-3 border-l-2 border-muted pl-4'>
      <div className='flex items-center justify-between gap-3'>
        <span className='text-xs font-medium text-muted-foreground'>
          Inboxes
        </span>
        <div className='flex items-center gap-2'>
          {/* Remounts when the offered list changes length, so a pick reverts the trigger
              to its placeholder instead of showing an inbox that just left the list. */}
          <Select
            key={shareable.length}
            disabled={!unitIsActive || shareable.length === 0}
            onValueChange={shareExisting}
          >
            <SelectTrigger size='sm' className='w-[190px] shrink-0'>
              <SelectValue placeholder='Add existing inbox…' />
            </SelectTrigger>
            <SelectContent>
              {shareable.map((inbox) => (
                <SelectItem key={inbox.id} value={String(inbox.id)}>
                  {inbox.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <InboxDialog
            businessUnit={businessUnit}
            trigger={
              <Button variant='outline' size='sm'>
                Add inbox
              </Button>
            }
          />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className='h-8 w-full' />
      ) : error ? (
        <p className='text-xs text-destructive'>
          {error instanceof Error ? error.message : 'Could not load inboxes.'}
        </p>
      ) : inboxes.length === 0 ? (
        <p className='rounded-md border border-dashed px-3 py-3 text-xs text-muted-foreground'>
          No inboxes yet. Add one for each destination whose tickets belong to{' '}
          {businessUnit.name}.
        </p>
      ) : (
        <ul className='space-y-2'>
          {inboxes.map((inbox) => {
            const busy = busyId === inbox.id;
            const primary = isPrimaryUnit(inbox, businessUnit.id);
            return (
              // Two lines: the name with its actions, then what the inbox is (`InboxFacts`).
              // On one line the tags and the buttons were look-alike pills.
              <li key={inbox.id} className='space-y-1.5 rounded-md border bg-card px-3 py-2'>
                <div className='flex items-center gap-2'>
                  <span className='min-w-0 flex-1 truncate text-sm font-medium'>
                    {inbox.name}
                  </span>
                  {/* The primary row keeps Edit and Delete: those act on the inbox itself, and
                      a non-primary row is just this unit's claim on it, not the inbox's home.
                      Row actions are outlined so they read as buttons, like "Add inbox" above;
                      Delete takes the red outline. */}
                  {primary ? (
                    <>
                      <InboxSharingDialog
                        inbox={inbox}
                        trigger={
                          <Button variant='outline' size='sm' disabled={busy}>
                            Share
                          </Button>
                        }
                      />
                      <InboxDialog
                        businessUnit={businessUnit}
                        inbox={inbox}
                        trigger={
                          <Button variant='outline' size='sm'>
                            Edit
                          </Button>
                        }
                      />
                      {/* "Unassign brand", not "Unassign": this detaches the inbox from the
                          business unit and nothing else. Detaching the CHANNEL DESTINATION is a
                          separate act, and it lives behind Edit — the two were both called
                          "unassign" and an operator wanting to drop a destination pressed this
                          one and moved the inbox out of its brand instead. */}
                      <Button
                        variant='outline'
                        size='sm'
                        disabled={busy}
                        onClick={() => unassign(inbox)}
                      >
                        Unassign brand
                      </Button>
                      {/* Behind a confirmation because it is the one irreversible action on
                          this row. Machine still refuses an inbox with conversations, so a
                          mis-click cannot strand them — the dialog is for the empty ones,
                          which really do disappear. */}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant='destructive' size='sm' disabled={busy}>
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {inbox.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Removes the inbox and the channel row it owns. This
                              cannot be undone, and anything still pointing at this
                              destination stops resolving to a brand. An inbox that
                              holds conversations is refused — empty it first, or
                              unassign it instead to keep it around without a brand.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(inbox.id, inbox.name)}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  ) : (
                    <>
                      {unitIsActive && (
                        <PrimaryChangeConfirm
                          inboxName={inbox.name}
                          toUnitName={businessUnit.name}
                          fromUnitName={allUnits.find((u) => u.id === inbox.bu_id)?.name ?? null}
                          onConfirm={() => makePrimary(inbox)}
                          trigger={
                            <Button variant='outline' size='sm' disabled={busy}>
                              Make primary
                            </Button>
                          }
                        />
                      )}
                      <InboxSharingDialog
                        inbox={inbox}
                        trigger={
                          <Button variant='outline' size='sm' disabled={busy}>
                            Share
                          </Button>
                        }
                      />
                      <Button
                        variant='outline'
                        size='sm'
                        disabled={busy}
                        onClick={() => unassign(inbox)}
                      >
                        Unassign brand
                      </Button>
                    </>
                  )}
                </div>
                <InboxFacts inbox={inbox} businessUnit={businessUnit} allUnits={allUnits} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
